"use client";

import { useState, useEffect } from "react";
import { Button } from "@/components/website/ui/Button";
import { FormField, inputClass } from "./FormField";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import {
  submitBookDemoAsync,
  resetDemoState,
} from "@/features/demo/slices/demo-slice";

export function DemoForm() {
  const dispatch = useAppDispatch();
  const { loading: isSubmitting, error: errorMessage, success: isSuccess } =
    useAppSelector((state) => state.demo);

  // State for form fields
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [role, setRole] = useState("");
  const [useCase, setUseCase] = useState("");
  const [anythingToCover, setAnythingToCover] = useState("");

  const [submittedName, setSubmittedName] = useState("");
  const [submittedEmail, setSubmittedEmail] = useState("");

  // Reset state on mount to clear any leftover global demo states
  useEffect(() => {
    dispatch(resetDemoState());
  }, [dispatch]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!fullName || !email) {
      return;
    }

    setSubmittedName(fullName);
    setSubmittedEmail(email);

    const resultAction = await dispatch(
      submitBookDemoAsync({
        fullName,
        email,
        company,
        role,
        useCase,
        anythingToCover,
      })
    );

    if (submitBookDemoAsync.fulfilled.match(resultAction)) {
      setFullName("");
      setEmail("");
      setCompany("");
      setRole("");
      setUseCase("");
      setAnythingToCover("");
    }
  };

  const handleReset = () => {
    dispatch(resetDemoState());
    setSubmittedName("");
    setSubmittedEmail("");
  };

  if (isSuccess) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center transition-all duration-300 animate-fade-in">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-website-accent text-text mb-6 shadow-sm">
          <svg
            className="h-8 w-8"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2.5"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <h3 className="font-display text-2xl font-bold tracking-tight text-text">
          Thank you, {submittedName}!
        </h3>
        <p className="mt-4 text-sm leading-relaxed text-muted max-w-sm">
          We&apos;ve received your request for a demo. A Reimon Energy specialist
          will contact you at{" "}
          <strong className="text-text font-medium">{submittedEmail}</strong>{" "}
          within one business day.
        </p>
        <button
          type="button"
          onClick={handleReset}
          className="mt-8 text-xs font-semibold uppercase tracking-wider text-accent-dark hover:text-text transition-colors cursor-pointer"
        >
          ← Submit another request
        </button>
      </div>
    );
  }

  if (errorMessage && !isSubmitting) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center transition-all duration-300 animate-fade-in">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100 text-red-600 mb-6 shadow-sm">
          <svg
            className="h-8 w-8"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <h3 className="font-display text-2xl font-bold tracking-tight text-text">
          Submission Failed
        </h3>
        <p className="mt-4 text-sm leading-relaxed text-muted max-w-sm">
          {errorMessage}
        </p>
        <button
          type="button"
          onClick={handleReset}
          className="mt-8 text-xs font-semibold uppercase tracking-wider text-accent-dark hover:text-text transition-colors cursor-pointer"
        >
          ← Try again
        </button>
      </div>
    );
  }

  return (
    <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField label="Full name" required>
          <input
            className={inputClass}
            placeholder="Jane Doe"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
            disabled={isSubmitting}
          />
        </FormField>
        <FormField label="Work email" required>
          <input
            className={inputClass}
            type="email"
            placeholder="jane@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={isSubmitting}
          />
        </FormField>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <FormField label="Company">
          <input
            className={inputClass}
            placeholder="Company name"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            disabled={isSubmitting}
          />
        </FormField>
        <FormField label="Role">
          <input
            className={inputClass}
            placeholder="Trader, Analyst…"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            disabled={isSubmitting}
          />
        </FormField>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <FormField label="What best describes your use case?">
          <select
            className={`${inputClass} appearance-none`}
            value={useCase}
            onChange={(e) => setUseCase(e.target.value)}
            disabled={isSubmitting}
          >
            <option value="" disabled>
              Select a use case…
            </option>
            <option value="Price forecasting">Price forecasting</option>
            <option value="Load forecasting">Load forecasting</option>
            <option value="Generation forecasting">Generation forecasting</option>
            <option value="Asset optimization">Asset optimization</option>
            <option value="Other">Other</option>
          </select>
        </FormField>
      </div>

      <FormField label="Anything to cover?">
        <textarea
          className={`${inputClass} min-h-[83px] resize-y`}
          placeholder="Optional"
          value={anythingToCover}
          onChange={(e) => setAnythingToCover(e.target.value)}
          disabled={isSubmitting}
        />
      </FormField>

      <Button type="submit" fullWidth className="py-3" disabled={isSubmitting}>
        {isSubmitting ? (
          <span className="flex items-center justify-center gap-2">
            <svg
              className="animate-spin h-5 w-5 text-text"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            Sending...
          </span>
        ) : (
          "Request demo →"
        )}
      </Button>

      <p className="text-center text-sm text-muted">
        No commitment. We&apos;ll reach out within one business day.
      </p>
    </form>
  );
}
